/**
 * Text-to-speech — duas estratégias:
 * 1. OpenAI TTS (qualidade alta, paga, pt-BR ok) — usado quando há chave
 * 2. Web Speech API (browser nativa, free, qualidade média) — fallback
 */

export type TTSVoice =
  | "alloy" | "echo" | "fable" | "onyx" | "nova" | "shimmer"
  | "browser-default";

const OPENAI_TTS_ENDPOINT = "https://api.openai.com/v1/audio/speech";

export interface SpeakOpts {
  text: string;
  voice?: TTSVoice;
  /** velocidade 0.25 - 4.0 (default 1.0) */
  speed?: number;
  /** chave OpenAI (se vazia, usa Web Speech API) */
  apiKey?: string;
}

/** Retorna um Blob de áudio mp3 do OpenAI TTS. */
export async function ttsOpenAI(opts: SpeakOpts): Promise<Blob> {
  if (!opts.apiKey) throw new Error("Sem chave OpenAI.");
  const voice = opts.voice && opts.voice !== "browser-default" ? opts.voice : "onyx";
  const res = await fetch(OPENAI_TTS_ENDPOINT, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "authorization": `Bearer ${opts.apiKey}`
    },
    body: JSON.stringify({
      model: "tts-1-hd",
      input: opts.text.slice(0, 4000),
      voice,
      speed: opts.speed ?? 1.0,
      response_format: "mp3"
    })
  });
  if (!res.ok) throw new Error(`OpenAI TTS ${res.status}: ${(await res.text()).slice(0, 200)}`);
  return await res.blob();
}

/** Tocador unificado — devolve { play, pause, stop, audio } */
export interface SpeakController {
  audio?: HTMLAudioElement;
  utterance?: SpeechSynthesisUtterance;
  play: () => void;
  pause: () => void;
  stop: () => void;
  isOpenAI: boolean;
  /** promessa que resolve quando termina */
  done: Promise<void>;
}

export async function speak(opts: SpeakOpts): Promise<SpeakController> {
  if (opts.apiKey && opts.voice !== "browser-default") {
    // OpenAI
    const blob = await ttsOpenAI(opts);
    const url = URL.createObjectURL(blob);
    const audio = new Audio(url);
    audio.playbackRate = opts.speed ?? 1.0;
    let resolveDone: () => void;
    const done = new Promise<void>(res => { resolveDone = res; });
    audio.addEventListener("ended", () => { resolveDone(); URL.revokeObjectURL(url); });
    audio.play();
    return {
      audio,
      play: () => audio.play(),
      pause: () => audio.pause(),
      stop: () => { audio.pause(); audio.currentTime = 0; URL.revokeObjectURL(url); resolveDone(); },
      isOpenAI: true,
      done
    };
  }
  // Web Speech API fallback
  return new Promise((resolve) => {
    const utter = new SpeechSynthesisUtterance(opts.text);
    utter.lang = "pt-BR";
    utter.rate = opts.speed ?? 1.0;
    // tenta voz pt-BR
    const voices = window.speechSynthesis.getVoices();
    const ptVoice = voices.find(v => /pt-?br/i.test(v.lang)) || voices.find(v => /portuguese/i.test(v.name));
    if (ptVoice) utter.voice = ptVoice;
    let resolveDone: () => void;
    const done = new Promise<void>(res => { resolveDone = res; });
    utter.onend = () => resolveDone();
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utter);
    resolve({
      utterance: utter,
      play: () => window.speechSynthesis.resume(),
      pause: () => window.speechSynthesis.pause(),
      stop: () => { window.speechSynthesis.cancel(); resolveDone(); },
      isOpenAI: false,
      done
    });
  });
}

export const TTS_VOICES: { id: TTSVoice; label: string }[] = [
  { id: "onyx",    label: "🎙️ Onyx — masculina grave, premium" },
  { id: "nova",    label: "🎙️ Nova — feminina vibrante" },
  { id: "shimmer", label: "🎙️ Shimmer — feminina suave" },
  { id: "echo",    label: "🎙️ Echo — masculina neutra" },
  { id: "fable",    label: "🎙️ Fable — narrador britânico" },
  { id: "alloy",   label: "🎙️ Alloy — neutra balanceada" },
  { id: "browser-default", label: "🔊 Voz do navegador (grátis, pt-BR meh)" }
];
